import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from './cn';
import { FOCUS_RING } from './Button';

/**
 * Tabela do Atmos.
 *
 * Densa e sóbria: cabeçalho rebaixado, linhas separadas por
 * `--border-subtle`, hover na linha inteira e números tabulares.
 * Rola dentro do próprio contêiner — a página nunca rola na horizontal.
 */
export function Table({
  bare,
  className,
  children,
  ...props
}: React.TableHTMLAttributes<HTMLTableElement> & {
  /** Sem borda/sombra própria — para quando a tabela já vive dentro de um Card. */
  bare?: boolean;
}) {
  return (
    <div
      className={cn(
        'w-full overflow-x-auto',
        !bare && 'rounded-xl border border-line bg-surface shadow-xs',
      )}
    >
      <table className={cn('w-full border-collapse text-[13.5px]', className)} {...props}>
        {children}
      </table>
    </div>
  );
}

export function THead({ children }: { children: React.ReactNode }) {
  return <thead className="bg-sunken">{children}</thead>;
}

export function TH({
  align = 'left',
  className,
  children,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement> & { align?: 'left' | 'right' | 'center' }) {
  return (
    <th
      className={cn(
        'whitespace-nowrap border-b border-line px-3.5 py-2.5 text-[11.5px] font-semibold uppercase tracking-[0.05em] text-muted',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        align === 'left' && 'text-left',
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function TBody({ children }: { children: React.ReactNode }) {
  return <tbody>{children}</tbody>;
}

export function TR({
  interactive,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement> & { interactive?: boolean }) {
  return (
    <tr
      className={cn(
        'border-b border-line-subtle last:border-0',
        interactive && 'cursor-pointer transition-colors duration-[120ms] hover:bg-hover',
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

export function TD({
  align = 'left',
  numeric,
  className,
  children,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  align?: 'left' | 'right' | 'center';
  numeric?: boolean;
}) {
  return (
    <td
      className={cn(
        'px-3.5 py-2.5 text-fg-2',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        numeric && 'font-mono tabular-nums',
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
}

/** Paginação — contagem à esquerda, controles à direita. */
export function Pagination({
  page,
  pageCount,
  total,
  pageSize,
  onChange,
  className,
}: {
  page: number;
  pageCount: number;
  /** Total de registros; se omitido, o resumo não aparece. */
  total?: number;
  pageSize?: number;
  onChange: (page: number) => void;
  className?: string;
}) {
  if (pageCount <= 1 && !total) return null;

  const from = pageSize ? (page - 1) * pageSize + 1 : null;
  const to = pageSize && total ? Math.min(page * pageSize, total) : null;

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      <span className="text-[12.5px] text-muted">
        {total !== undefined && from !== null && to !== null
          ? `${from}–${to} de ${total.toLocaleString('pt-BR')}`
          : `Página ${page} de ${pageCount}`}
      </span>
      <div className="flex items-center gap-1">
        <PageButton
          label="Anterior"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          <ChevronLeft size={14} />
        </PageButton>
        {pageWindow(page, pageCount).map((p, i) =>
          p === null ? (
            <span key={`gap-${i}`} className="px-1 text-[12.5px] text-muted">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              aria-current={p === page ? 'page' : undefined}
              className={cn(
                'h-7 min-w-7 rounded-[7px] border px-2 text-[12.5px] tabular-nums transition-colors duration-[120ms]',
                FOCUS_RING,
                p === page
                  ? 'border-primary-line bg-primary-soft font-semibold text-primary'
                  : 'border-line bg-surface text-fg-2 hover:bg-hover',
              )}
            >
              {p}
            </button>
          ),
        )}
        <PageButton
          label="Próxima"
          disabled={page >= pageCount}
          onClick={() => onChange(page + 1)}
        >
          <ChevronRight size={14} />
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'grid h-7 w-7 place-items-center rounded-[7px] border border-line bg-surface text-fg-2 transition-colors duration-[120ms] hover:bg-hover',
        'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-faint disabled:hover:bg-sunken',
        FOCUS_RING,
      )}
    >
      {children}
    </button>
  );
}

/** Janela de páginas: sempre primeira, última e as vizinhas da atual. */
function pageWindow(page: number, pageCount: number): Array<number | null> {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const pages = new Set([1, pageCount, page, page - 1, page + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
  const out: Array<number | null> = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push(null);
    out.push(p);
  });
  return out;
}
